import type { AuditFindingPartial, AuditRule } from "../types";
import { addressFromRC } from "../addressing";

/**
 * Within each column, group formulas by R1C1 pattern id (a workbook-wide
 * intern key — relative offsets make this canonical). Flag cells whose
 * pattern is a minority within the column — they're likely typos or
 * one-off overrides in an otherwise uniform column.
 */

const MIN_COLUMN_FORMULAS = 3;
const MAJORITY_THRESHOLD = 0.6;

export const inconsistentFormulas: AuditRule = {
  id: "system.audit.inconsistent-formulas",
  name: "Inconsistent formulas in a column",
  description:
    "Within each column, a cell whose formula shape differs from the majority — often a typo or a one-off override.",
  enabled: true,
  source: "system",
  severity: "warning",
  /**
   * R1C1 is canonical by construction (encodes references as offsets
   * from the host cell), so grouping by r1c1Ids gives the same result
   * the legacy canonicalize() was approximating — but with integer
   * equality instead of regex-substituted string compares, and
   * correctly for cases A1 canonicalize missed (range refs, function
   * calls, cross-sheet refs).
   *
   * Pattern grouping is Map<patternId, count> over Int32Array reads.
   */
  check(sheet, model) {
    const findings: AuditFindingPartial[] = [];
    const { rows, cols, startRow, startCol } = sheet.bounds;
    const r1c1Ids = sheet.r1c1Ids;
    const r1c1Strings = model.catalogs.r1c1Strings;

    for (let c = 0; c < cols; c++) {
      // Collect (row, patternId) for formula cells in this column
      const cells: Array<{ row: number; patternId: number }> = [];
      for (let r = 0; r < rows; r++) {
        const id = r1c1Ids[r * cols + c];
        if (id < 0) continue;
        cells.push({ row: r, patternId: id });
      }
      if (cells.length < MIN_COLUMN_FORMULAS) continue;

      // Count patterns
      const countByPattern = new Map<number, number>();
      for (const cell of cells) {
        countByPattern.set(cell.patternId, (countByPattern.get(cell.patternId) ?? 0) + 1);
      }

      // Find the dominant pattern
      let majority = -1;
      let majorityCount = 0;
      for (const [id, count] of Array.from(countByPattern)) {
        if (count > majorityCount) {
          majority = id;
          majorityCount = count;
        }
      }
      if (majority < 0 || majorityCount / cells.length < MAJORITY_THRESHOLD) continue;

      for (const cell of cells) {
        if (cell.patternId === majority) continue;
        findings.push({
          sheetName: sheet.name,
          address: addressFromRC(startRow + cell.row, startCol + c),
          // R1C1 form is what's in the model. The user sees R1C1 in the
          // message; the address column points them to the cell so they
          // can inspect the A1 form in Excel directly.
          message: `formula "${r1c1Strings[cell.patternId]}" (R1C1) differs from column majority shape`,
        });
      }
    }

    return findings;
  },
};
